// Local SEO content per city we service.
// Each entry powers a dedicated /service-area/:slug page with unique content, meta and JSON-LD.

const HERO_CRANE = "https://images.pexels.com/photos/9906887/pexels-photo-9906887.jpeg";
const HERO_GROVE = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/fabzdy7b_379E0A4F-9557-483E-91F7-55A4134B3FAF-800x600.jpeg";
const HERO_MANITEX = "https://customer-assets.emergentagent.com/job_heavy-lift-solutions-3/artifacts/08j7l5sd_IMG_4360.jpeg";

export const CITY_CONTENT = {
  "deland": {
    name: "DeLand",
    slug: "deland",
    tagline: "Our Home Base",
    county: "Volusia County",
    zip: "32720",
    lat: 29.0283, lng: -81.3031,
    distance: "0 mi",
    driveTime: "Local",
    metaTitle: "Crane Rental in DeLand, FL — Alliance Crane Service",
    metaDescription:
      "Local mobile crane rental in DeLand, Florida. Alliance Crane Service is headquartered in DeLand — 40-ton Grove & 50-ton Manitex cranes for HVAC lifts, steel erection, trusses, machinery moving & 24/7 emergency service.",
    keywords:
      "crane rental deland fl, deland crane service, mobile crane deland florida, deland hvac lift, deland truss crane, volusia county crane rental",
    heroImage: HERO_GROVE,
    intro:
      "DeLand is home base for Alliance Crane Service. Our yard, our shop and our dispatch all sit right here in Volusia County — which means DeLand contractors get the fastest response times and the most flexible scheduling of any city we serve.",
    coverage:
      "Local means local. Same-day mobilization is standard on the DeLand side of the county for standard picks, and our emergency line answers 24/7 for tree-on-house, storm response and structural failures anywhere in the city.",
    localNotes: [
      "We work every corner of DeLand — from historic downtown near Stetson University to the industrial parks along Woodland Boulevard, the new residential communities pushing west toward Cassadaga, and the light-industrial and mechanical yards near the DeLand Municipal Airport.",
      "Local contractors we regularly partner with include mechanical, roofing, framing and steel-building crews servicing single-family builds, agricultural buildings, warehouses and small retail projects throughout Volusia County.",
    ],
    commonProjects: [
      "Residential HVAC change-outs and rooftop unit sets",
      "New home truss lifts across west Volusia",
      "Pre-engineered metal building erection near the airport",
      "Church and community-building steel picks",
      "Tree-on-house emergency response after summer storms",
      "Generator and equipment sets at commercial and industrial sites",
    ],
    neighborhoods: [
      "Downtown DeLand", "Stetson University area", "Cassadaga Rd corridor",
      "Woodland Blvd", "DeLand Municipal Airport area", "Glenwood",
      "West Volusia", "Orange City edge",
    ],
    nearbyAreas: ["sanford", "lake-mary", "ormond-beach", "daytona-beach"],
    faqs: [
      { q: "Are you actually based in DeLand?", a: "Yes — Alliance Crane Service is a family-operated company headquartered in DeLand, Florida (Volusia County). Our cranes yard here between jobs." },
      { q: "How fast can you get to a DeLand jobsite?", a: "For non-emergency work, same-day mobilization is standard on the DeLand side of Volusia. Emergency calls are typically onsite in 30–60 minutes." },
      { q: "Do you work with local DeLand contractors and homeowners?", a: "Both. Our operated crane rentals serve GCs, mechanical subs, framers, roofers, homeowners doing tree-removal, HVAC change-outs and small commercial jobs alike." },
    ],
  },

  "orlando": {
    name: "Orlando",
    slug: "orlando",
    tagline: "The Central Florida Hub",
    county: "Orange County",
    zip: "32801",
    lat: 28.5383, lng: -81.3792,
    distance: "35 mi south of DeLand",
    driveTime: "~45 min",
    metaTitle: "Crane Rental in Orlando, FL — Mobile Cranes with Operator",
    metaDescription:
      "Mobile crane rental in Orlando, FL from Alliance Crane Service. 40-ton Grove & 50-ton Manitex with certified operators for HVAC, steel erection, machinery moving and 24/7 emergency lifting in Central Florida.",
    keywords:
      "crane rental orlando fl, orlando crane service, mobile crane orlando, hvac crane lift orlando, orlando steel erection crane, orange county crane rental",
    heroImage: HERO_MANITEX,
    intro:
      "Orlando is the beating heart of Central Florida construction — retail buildouts, restaurant TIs, hospitality expansions, warehouse rows along the 528, and constant HVAC and mechanical work in tourist corridors. Alliance Crane Service runs into Orlando multiple times a week from our DeLand yard.",
    coverage:
      "About 45 minutes south of DeLand via I-4, Orlando is fully in our daily working radius. We book same-week when possible and can quote urgent same-day picks depending on the queue. Our 24/7 emergency line covers Orlando storms, HVAC failures and rooftop equipment breakdowns year-round.",
    localNotes: [
      "We handle every side of Orlando: downtown mid-rise tenant improvements, warehouse and industrial development along the 528 and I-4, hospitality and hotel work around the tourist corridors, medical district equipment sets, and endless HVAC and mechanical projects across office parks and retail.",
      "Whether it is a Saturday-morning rooftop-unit swap on a restaurant off Sand Lake Road, staging steel for a warehouse in southeast Orlando, or dropping a generator at an office park in downtown, we bring the right crane for the pick and the right operator for the job.",
    ],
    commonProjects: [
      "Rooftop HVAC unit swaps for retail and restaurant chains",
      "Chiller and cooling-tower placements",
      "Warehouse and light-industrial steel erection",
      "Sign, monument and light-pole installations",
      "Generator and transformer sets at office parks",
      "Emergency response for storms and equipment failures",
    ],
    neighborhoods: [
      "Downtown Orlando", "Winter Park edge", "Baldwin Park",
      "Lake Nona", "Millenia", "Sand Lake / I-Drive",
      "College Park", "Southeast Orlando", "Metrowest",
    ],
    nearbyAreas: ["sanford", "lake-mary", "kissimmee", "melbourne"],
    faqs: [
      { q: "Do you drive to Orlando from DeLand?", a: "Yes — Orlando is one of our most-served markets. It is about 45 minutes down I-4 from our yard, and we schedule Orlando jobs alongside DeLand and Daytona work daily." },
      { q: "Can you handle downtown Orlando picks with limited access?", a: "Yes. Our 40-ton Grove is compact enough for tight downtown lots and one-lane closures. We coordinate with your GC on street permits and traffic control when required." },
      { q: "Do you do after-hours or weekend HVAC lifts in Orlando?", a: "Regularly. Retail and restaurant HVAC swaps often happen off-hours to protect occupancy — we accommodate evenings and weekends when the schedule allows." },
    ],
  },

  "daytona-beach": {
    name: "Daytona Beach",
    slug: "daytona-beach",
    tagline: "Coastal & Commercial Lifts",
    county: "Volusia County",
    zip: "32114",
    lat: 29.2108, lng: -81.0228,
    distance: "22 mi east of DeLand",
    driveTime: "~30 min",
    metaTitle: "Crane Rental in Daytona Beach, FL — Mobile Crane Services",
    metaDescription:
      "Mobile crane rental in Daytona Beach, FL. Alliance Crane Service runs 40-ton Grove & 50-ton Manitex cranes for HVAC, steel erection, machinery moving, coastal construction and 24/7 emergency lifting.",
    keywords:
      "crane rental daytona beach, daytona crane service, mobile crane daytona, hvac lift daytona, daytona steel erection, volusia county crane",
    heroImage: HERO_CRANE,
    intro:
      "Daytona Beach is 22 short miles east of our DeLand yard — a straight shot down 92. Between Daytona proper, Daytona Beach Shores, Port Orange and the beachside neighborhoods, we run into the market constantly for HVAC, condo work, commercial buildouts and hurricane-season emergency response.",
    coverage:
      "Under a 30-minute drive from home, Daytona is essentially local territory. Same-week booking is easy and same-day is often achievable. Our 24/7 line handles beachside storm response, tree-on-house and structural stabilization year-round — especially critical during hurricane season.",
    localNotes: [
      "Daytona work runs the gamut — condo and hotel HVAC replacements on the beachside, retail and restaurant chain work along International Speedway Boulevard, medical facility equipment sets near Halifax Health, and steel erection for light-industrial development inland toward US-92.",
      "We know the coastal wind considerations, the salt-air maintenance realities and the tight access windows that come with beachside work. Weekend and off-hours lifts are common to protect condo occupancy.",
    ],
    commonProjects: [
      "Beachside condo HVAC replacements",
      "Hotel and hospitality rooftop equipment sets",
      "Retail and restaurant chain HVAC work",
      "Storm-response tree and structural lifts",
      "Post-hurricane roof and building stabilization",
      "Speedway-corridor commercial buildouts",
    ],
    neighborhoods: [
      "Daytona Beach Shores", "Beachside", "Ormond-by-the-Sea edge",
      "Port Orange edge", "ISB corridor", "Halifax medical district",
      "South Daytona", "Wilbur-by-the-Sea",
    ],
    nearbyAreas: ["ormond-beach", "new-smyrna-beach", "deland"],
    faqs: [
      { q: "How quickly can you reach Daytona from DeLand?", a: "About 30 minutes normal traffic. Non-emergency same-day booking is often possible and emergency calls are on-site quickly." },
      { q: "Do you handle beachside condo HVAC work?", a: "Yes — condo and hotel rooftop unit swaps are one of our regular scopes in Daytona. We coordinate weekend and off-hours windows to protect occupancy." },
      { q: "Can you respond to hurricane damage in Daytona?", a: "Yes. We prioritize life-safety and structural stabilization calls first, then work insured-property calls in queue order. Book early — do not wait for the storm to make landfall." },
    ],
  },

  "jacksonville": {
    name: "Jacksonville",
    slug: "jacksonville",
    tagline: "Northeast Florida Coverage",
    county: "Duval County",
    zip: "32202",
    lat: 30.3322, lng: -81.6557,
    distance: "90 mi north of DeLand",
    driveTime: "~1h 30min",
    metaTitle: "Crane Rental in Jacksonville, FL — Alliance Crane Service",
    metaDescription:
      "Mobile crane rental in Jacksonville, FL. 40-ton Grove & 50-ton Manitex cranes with certified operators for HVAC, steel erection, machinery moving and emergency lifting across Duval County.",
    keywords:
      "crane rental jacksonville fl, jacksonville crane service, mobile crane jacksonville, jacksonville hvac lift, duval county crane rental, northeast florida crane",
    heroImage: HERO_MANITEX,
    intro:
      "Jacksonville is our northern reach — a ~90-mile run up I-95 that puts us in Duval County within ninety minutes. We serve Jacksonville with the same equipment, the same operators and the same standards we use at home, and we quote mobilization transparently up front.",
    coverage:
      "For scheduled work, Jacksonville is comfortably within our daily working range. We batch Jacksonville jobs when we can to keep mobilization efficient. Emergency and unplanned lifts are quoted case-by-case with clear travel time.",
    localNotes: [
      "Jacksonville projects tend to be larger commercial and light-industrial — warehouses along Beach Boulevard and 295, HVAC and equipment sets in office parks along the Southside, and mechanical work at hospitals and medical office buildings throughout the metro.",
      "We also cover the beaches — Jacksonville Beach, Neptune Beach and Atlantic Beach — for coastal HVAC, condo and light-commercial work when the schedule aligns.",
    ],
    commonProjects: [
      "Commercial HVAC and RTU sets",
      "Warehouse and distribution center steel picks",
      "Medical office equipment placements",
      "Sign and lighting installations along commercial corridors",
      "Chiller and cooling-tower lifts",
      "Machinery moving for light-industrial facilities",
    ],
    neighborhoods: [
      "Downtown Jacksonville", "Southside", "Riverside",
      "San Marco", "Mandarin", "Jacksonville Beach",
      "Neptune Beach", "Atlantic Beach", "Baymeadows",
    ],
    nearbyAreas: ["palm-coast", "daytona-beach", "deland"],
    faqs: [
      { q: "Do you actually service Jacksonville from DeLand?", a: "Yes. Jacksonville is a ~90-mile run up I-95. We schedule Jacksonville work into our weekly runs and quote mobilization transparently so there are no surprises." },
      { q: "Is mobilization expensive to Jacksonville?", a: "It is a longer drive than our core Central Florida markets, so mobilization is quoted upfront. We do our best to batch nearby jobs to reduce your cost." },
      { q: "Can you handle multi-day Jacksonville projects?", a: "Yes. For steel erection and multi-day scopes we can stage in the area to reduce daily mob and keep the pace up." },
    ],
  },

  "titusville": {
    name: "Titusville",
    slug: "titusville",
    tagline: "Space Coast Lifts",
    county: "Brevard County",
    zip: "32780",
    lat: 28.6122, lng: -80.8076,
    distance: "50 mi southeast of DeLand",
    driveTime: "~1h",
    metaTitle: "Crane Rental in Titusville, FL — Space Coast Crane Service",
    metaDescription:
      "Mobile crane rental in Titusville, FL and the Space Coast. Alliance Crane Service brings 40-ton Grove & 50-ton Manitex cranes for HVAC, machinery moving, steel erection and emergency lifting in Brevard County.",
    keywords:
      "crane rental titusville fl, titusville crane service, space coast crane rental, brevard county crane, mobile crane titusville, hvac lift titusville",
    heroImage: HERO_GROVE,
    intro:
      "Titusville sits at the northern edge of the Space Coast — a growing mix of aerospace, light-industrial, retail and residential development. Alliance Crane Service reaches Titusville in about an hour from DeLand, and we regularly service Brevard County construction crews on scheduled work.",
    coverage:
      "About an hour southeast of our yard via SR-46 and I-95, Titusville is a comfortable day-trip market. We schedule Titusville jobs alongside our Melbourne and Cocoa work when possible to make mobilization efficient.",
    localNotes: [
      "Titusville work skews toward light-industrial and commercial: warehouse construction along US-1 and Garden Street, HVAC replacements in schools and office buildings, retail and restaurant chain work, and equipment moves for aerospace-supply companies serving the Space Coast.",
      "We also handle residential and church work in Titusville and the surrounding communities as scheduling allows.",
    ],
    commonProjects: [
      "Warehouse and light-industrial steel erection",
      "HVAC replacements for schools and commercial buildings",
      "Retail and restaurant rooftop equipment sets",
      "Aerospace-supply machinery moves and equipment sets",
      "Sign and monument installations",
      "Storm response for Space Coast weather events",
    ],
    neighborhoods: [
      "Downtown Titusville", "US-1 corridor", "Garden Street",
      "Sand Point Park area", "Whispering Hills", "South Titusville",
      "Mims edge", "Port St. John edge",
    ],
    nearbyAreas: ["melbourne", "kissimmee", "orlando", "deland"],
    faqs: [
      { q: "How far is Titusville from your DeLand yard?", a: "About 50 miles / one hour via SR-46 and I-95. We reach Titusville regularly and can schedule Space Coast work within a normal booking window." },
      { q: "Do you work with Space Coast aerospace suppliers?", a: "Yes — we handle machinery moving and equipment placement for light-industrial and aerospace-supply facilities in the Titusville area." },
      { q: "Can you handle HVAC in Titusville schools and commercial buildings?", a: "Absolutely. Rooftop-unit swaps and chiller sets in schools, offices and retail are one of our most common scopes across Brevard County." },
    ],
  },

  "sanford": {
    name: "Sanford",
    slug: "sanford",
    tagline: "North Seminole County",
    county: "Seminole County",
    zip: "32771",
    lat: 28.8028, lng: -81.2731,
    distance: "20 mi south of DeLand",
    driveTime: "~30 min",
    metaTitle: "Crane Rental in Sanford, FL — Mobile Crane Services",
    metaDescription:
      "Mobile crane rental in Sanford, FL. Alliance Crane Service — 40-ton Grove & 50-ton Manitex mobile cranes for HVAC, steel, machinery moving and emergency lifting in Seminole County.",
    keywords:
      "crane rental sanford fl, sanford crane service, mobile crane sanford, seminole county crane rental, sanford hvac lift",
    heroImage: HERO_CRANE,
    intro:
      "Sanford is one of our most convenient markets — only 20 miles south of DeLand on I-4 and 17-92. From the historic downtown to the industrial parks around the Orlando Sanford International Airport, we run into Sanford regularly for HVAC, steel and machinery work.",
    coverage:
      "About 30 minutes from our yard, Sanford is essentially day-trip territory. Same-week and often same-day booking is realistic for standard scopes.",
    localNotes: [
      "Sanford work includes airport-adjacent industrial development, waterfront and downtown redevelopment, retail and restaurant HVAC change-outs along 17-92, and residential truss work in newer communities on the east side of the city.",
      "We coordinate with mechanical, framing, roofing and steel-erection crews across Seminole County multiple times a month.",
    ],
    commonProjects: [
      "Airport-area industrial HVAC and equipment sets",
      "Downtown retail and restaurant rooftop work",
      "Residential truss lifts in east Sanford communities",
      "Machinery moving for light-industrial facilities",
      "Church and community-building steel picks",
      "Storm response and emergency lifting",
    ],
    neighborhoods: ["Downtown Sanford", "Sanford Airport area", "Lake Monroe", "17-92 corridor", "Historic District"],
    nearbyAreas: ["lake-mary", "deland", "orlando"],
    faqs: [
      { q: "How long from DeLand to Sanford?", a: "About 30 minutes via I-4 or 17-92. Same-day work is realistic depending on the schedule." },
      { q: "Do you cover the Sanford airport industrial area?", a: "Yes — light-industrial and airport-adjacent HVAC, equipment and machinery scopes are a regular part of our Seminole County work." },
    ],
  },

  "lake-mary": {
    name: "Lake Mary",
    slug: "lake-mary",
    tagline: "Corporate & Commercial",
    county: "Seminole County",
    zip: "32746",
    lat: 28.7589, lng: -81.3178,
    distance: "24 mi south of DeLand",
    driveTime: "~35 min",
    metaTitle: "Crane Rental in Lake Mary, FL — Corporate Corridor Crane Service",
    metaDescription:
      "Mobile crane rental in Lake Mary, FL. 40-ton Grove & 50-ton Manitex cranes for HVAC, office-park equipment sets, retail rooftop unit swaps and emergency lifting.",
    keywords:
      "crane rental lake mary fl, lake mary crane service, mobile crane lake mary, hvac lift lake mary, seminole county crane",
    heroImage: HERO_MANITEX,
    intro:
      "Lake Mary is Central Florida's corporate corridor — office parks, mid-rise buildings and retail centers packed along the I-4 corridor. Alliance Crane Service is a regular in Lake Mary for rooftop HVAC swaps, generator and equipment sets, and office-park mechanical work.",
    coverage:
      "About 35 minutes from DeLand, Lake Mary schedules easily into our daily runs alongside Sanford and north Orlando work.",
    localNotes: [
      "Most of our Lake Mary work is commercial: RTU replacements on Class-A office rooftops, chiller lifts, generator sets, retail and restaurant HVAC change-outs, and light steel work at office and retail buildouts.",
    ],
    commonProjects: [
      "Class-A office rooftop HVAC replacements",
      "Chiller and cooling-tower placements",
      "Generator and transformer sets",
      "Retail and restaurant HVAC work",
      "Office-park steel and canopy installations",
    ],
    neighborhoods: ["Lake Mary Blvd corridor", "Colonial TownPark", "I-4 corporate corridor", "Heathrow edge"],
    nearbyAreas: ["sanford", "orlando", "deland"],
    faqs: [
      { q: "Do you work with Lake Mary office-building property managers?", a: "Yes — we regularly coordinate directly with property managers and mechanical contractors for planned RTU swaps and emergency HVAC replacements on Lake Mary office rooftops." },
    ],
  },

  "ormond-beach": {
    name: "Ormond Beach",
    slug: "ormond-beach",
    tagline: "North Volusia Coast",
    county: "Volusia County",
    zip: "32174",
    lat: 29.2858, lng: -81.0559,
    distance: "26 mi east of DeLand",
    driveTime: "~35 min",
    metaTitle: "Crane Rental in Ormond Beach, FL — Coastal Crane Services",
    metaDescription:
      "Mobile crane rental in Ormond Beach, FL. Alliance Crane Service supports coastal HVAC, condo, retail and residential work with 40-ton and 50-ton mobile cranes.",
    keywords:
      "crane rental ormond beach, ormond beach crane service, mobile crane ormond, coastal hvac lift ormond, volusia county crane",
    heroImage: HERO_GROVE,
    intro:
      "Ormond Beach sits just north of Daytona and about 35 minutes from our DeLand yard. Between mainland residential and commercial and beachside condo work, we run into Ormond regularly for HVAC, roofing-crane assistance and post-storm response.",
    coverage:
      "Ormond is a comfortable same-week booking. Emergency storm work is priority-dispatched from DeLand or, when possible, batched off our Daytona runs.",
    localNotes: [
      "Ormond projects include beachside condo HVAC change-outs, mainland retail and restaurant work along Granada Boulevard, residential additions and truss lifts, and steady tree-response calls after summer storms.",
    ],
    commonProjects: [
      "Beachside condo HVAC lifts",
      "Granada Boulevard retail rooftop work",
      "Residential truss and roof lifts",
      "Storm response and tree-on-house",
      "Small commercial equipment sets",
    ],
    neighborhoods: ["Ormond-by-the-Sea", "Granada Blvd corridor", "Plantation Bay edge", "Beachside", "Mainland Ormond"],
    nearbyAreas: ["daytona-beach", "palm-coast", "deland"],
    faqs: [
      { q: "How fast can you reach Ormond Beach?", a: "About 35 minutes on a normal day. Emergency calls dispatch immediately." },
      { q: "Do you handle beachside Ormond condo HVAC?", a: "Yes — coastal condo rooftop-unit work is one of our regular Volusia County scopes." },
    ],
  },

  "new-smyrna-beach": {
    name: "New Smyrna Beach",
    slug: "new-smyrna-beach",
    tagline: "South Volusia Coast",
    county: "Volusia County",
    zip: "32168",
    lat: 29.0258, lng: -80.9270,
    distance: "40 mi southeast of DeLand",
    driveTime: "~50 min",
    metaTitle: "Crane Rental in New Smyrna Beach, FL — Coastal Crane Service",
    metaDescription:
      "Mobile crane rental in New Smyrna Beach, FL. Alliance Crane Service for coastal HVAC, condo, restaurant and residential work in south Volusia County.",
    keywords:
      "crane rental new smyrna beach, new smyrna crane service, mobile crane new smyrna, coastal crane volusia, hvac lift new smyrna",
    heroImage: HERO_CRANE,
    intro:
      "New Smyrna Beach is our south-Volusia coastal market — a busy mix of beachside condos, restaurants, small commercial and residential builds along the intracoastal. We reach NSB in under an hour and service it regularly for scheduled HVAC and storm response.",
    coverage:
      "Under an hour from our yard, New Smyrna schedules easily. Beachside access windows are respected and we work weekends when needed to protect condo occupancy.",
    localNotes: [
      "NSB work includes flagler-side condo HVAC change-outs, restaurant and hotel rooftop unit swaps along Flagler Avenue and Third Avenue, residential additions in Venetian Bay and inland communities, and steady tree and storm response through hurricane season.",
    ],
    commonProjects: [
      "Beachside condo and hotel HVAC lifts",
      "Flagler Avenue restaurant rooftop work",
      "Residential truss and roof-lift assistance",
      "Storm and tree-response emergency lifts",
      "Small commercial equipment sets",
    ],
    neighborhoods: ["Flagler Avenue", "Third Avenue", "Venetian Bay", "Coronado Island", "Beachside NSB"],
    nearbyAreas: ["daytona-beach", "deland"],
    faqs: [
      { q: "Do you cover New Smyrna Beach?", a: "Yes — under an hour from our DeLand yard. NSB is a regular part of our Volusia County service map." },
    ],
  },

  "kissimmee": {
    name: "Kissimmee",
    slug: "kissimmee",
    tagline: "South Orlando Metro",
    county: "Osceola County",
    zip: "34741",
    lat: 28.2920, lng: -81.4076,
    distance: "50 mi southwest of DeLand",
    driveTime: "~1h",
    metaTitle: "Crane Rental in Kissimmee, FL — Osceola County Crane Service",
    metaDescription:
      "Mobile crane rental in Kissimmee, FL. Alliance Crane Service — 40-ton Grove & 50-ton Manitex cranes for HVAC, hospitality and residential work in Osceola County.",
    keywords:
      "crane rental kissimmee fl, kissimmee crane service, mobile crane kissimmee, osceola county crane, hvac lift kissimmee",
    heroImage: HERO_MANITEX,
    intro:
      "Kissimmee sits south of Orlando in Osceola County — heavy on hospitality, residential development and retail. We schedule Kissimmee work into our Orlando-side runs and provide the same equipment and operator quality we bring to any Central Florida market.",
    coverage:
      "About an hour from DeLand via I-4. We batch Kissimmee jobs with Orlando work to keep mobilization efficient.",
    localNotes: [
      "Kissimmee work skews hospitality (hotel HVAC and rooftop equipment), residential (production home truss lifts) and light-commercial (retail chain rollouts and restaurant HVAC).",
    ],
    commonProjects: [
      "Hotel and resort HVAC replacements",
      "Production-home residential truss lifts",
      "Retail chain rooftop unit swaps",
      "Restaurant HVAC and MUA installations",
      "Sign and monument installs",
    ],
    neighborhoods: ["Downtown Kissimmee", "192 corridor", "Poinciana edge", "Celebration edge", "Buenaventura Lakes"],
    nearbyAreas: ["orlando", "melbourne", "deland"],
    faqs: [
      { q: "Do you cover the Kissimmee hotel corridor?", a: "Yes — Kissimmee-area hospitality HVAC and rooftop unit work is a regular scope for us." },
    ],
  },

  "melbourne": {
    name: "Melbourne",
    slug: "melbourne",
    tagline: "Central Space Coast",
    county: "Brevard County",
    zip: "32901",
    lat: 28.0836, lng: -80.6081,
    distance: "80 mi southeast of DeLand",
    driveTime: "~1h 30min",
    metaTitle: "Crane Rental in Melbourne, FL — Space Coast Crane Service",
    metaDescription:
      "Mobile crane rental in Melbourne, FL. Alliance Crane Service brings mobile cranes into Brevard County for HVAC, steel, machinery and light-industrial work.",
    keywords:
      "crane rental melbourne fl, melbourne crane service, brevard county crane, mobile crane melbourne, hvac lift melbourne",
    heroImage: HERO_MANITEX,
    intro:
      "Melbourne is a growing central Space Coast market — home to aerospace suppliers, light-industrial development, medical facilities and steady residential growth. Alliance Crane Service schedules Melbourne alongside Titusville and Palm Bay work when possible.",
    coverage:
      "About 90 minutes from DeLand. Mobilization quoted upfront; we batch when we can to reduce cost.",
    localNotes: [
      "Melbourne work includes aerospace-supplier machinery moves, medical facility equipment sets, warehouse and light-industrial HVAC, and commercial buildouts along US-1 and I-95.",
    ],
    commonProjects: [
      "Aerospace-supplier machinery moves",
      "Medical facility HVAC and equipment sets",
      "Warehouse and light-industrial steel",
      "Retail and restaurant rooftop unit work",
      "Sign and light-pole installations",
    ],
    neighborhoods: ["Downtown Melbourne", "US-1 corridor", "Wickham Road", "Suntree edge", "West Melbourne"],
    nearbyAreas: ["titusville", "kissimmee", "orlando"],
    faqs: [
      { q: "Do you service the Melbourne aerospace corridor?", a: "Yes — machinery moving and equipment placements for aerospace suppliers are one of our specialty scopes in Brevard County." },
    ],
  },

  "palm-coast": {
    name: "Palm Coast",
    slug: "palm-coast",
    tagline: "Flagler County",
    county: "Flagler County",
    zip: "32164",
    lat: 29.5850, lng: -81.2076,
    distance: "45 mi north of DeLand",
    driveTime: "~55 min",
    metaTitle: "Crane Rental in Palm Coast, FL — Flagler County Crane Service",
    metaDescription:
      "Mobile crane rental in Palm Coast, FL. Alliance Crane Service — mobile cranes for HVAC, residential, coastal and light-commercial work in Flagler County.",
    keywords:
      "crane rental palm coast fl, palm coast crane service, mobile crane palm coast, flagler county crane, hvac lift palm coast",
    heroImage: HERO_GROVE,
    intro:
      "Palm Coast is a fast-growing Flagler County market with a heavy mix of residential development and coastal commercial work. Alliance Crane Service runs into Palm Coast about an hour up from our DeLand yard.",
    coverage:
      "About 55 minutes from home. We schedule Palm Coast work into our northern runs and quote mobilization transparently.",
    localNotes: [
      "Palm Coast work skews residential truss lifts, HVAC replacements at retail centers along Palm Coast Parkway, coastal HVAC and light-commercial work, and steady storm response through hurricane season.",
    ],
    commonProjects: [
      "Residential truss lifts in new communities",
      "Retail center rooftop unit swaps",
      "Coastal HVAC replacements",
      "Storm and tree-response emergency lifts",
      "Small commercial equipment sets",
    ],
    neighborhoods: ["Palm Coast Parkway", "Grand Haven", "Hammock Dunes edge", "Matanzas Woods", "Palm Harbor"],
    nearbyAreas: ["ormond-beach", "jacksonville", "deland"],
    faqs: [
      { q: "How far is Palm Coast from your DeLand yard?", a: "About 55 minutes up I-95. We schedule Palm Coast work into our northern runs regularly." },
    ],
  },
};

// Ordered list for iteration (nav, service-area page, sitemap, footer)
export const CITY_SLUGS = [
  "deland", "orlando", "daytona-beach", "jacksonville", "titusville",
  "sanford", "lake-mary", "ormond-beach", "new-smyrna-beach",
  "kissimmee", "melbourne", "palm-coast",
];
